import React, { useCallback } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Checkbox from '@mui/material/Checkbox';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import OutlinedInput from '@mui/material/OutlinedInput';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import ISe360User from '../se360users/ISe360User';
import { Translate } from '../../shared/LanguageService';
import * as Features from '../../shared/Features';
import FirmModal from '../brokers/firm/FirmModal';
import AgentModal from '../brokers/agent/AgentModal';
import { STATUS_OPTIONS, formatCurrency } from './procountorInvoiceHelpers';
import { IProcountorInvoicePreviewResponse } from './ZentuvoInvoicingApi';
import { useProcountorPreview } from './hooks/useProcountorPreview';
import { useInvoiceSelection } from './hooks/useInvoiceSelection';
import { useGroupExpansion } from './hooks/useGroupExpansion';
import { useInvoiceListControls } from './hooks/useInvoiceListControls';
import { useProcountorIdGeneration } from './hooks/useProcountorIdGeneration';
import { useBrokerNodeEditors } from './hooks/useBrokerNodeEditors';
import { useInvoiceActions } from './hooks/useInvoiceActions';
import { useVisibleInvoiceGroups } from './hooks/useVisibleInvoiceGroups';
import InvoiceGroupAccordion from './components/InvoiceGroupAccordion';

interface IProps {
    selections: any;
    filters: any;
    list: any;
    user: ISe360User;
}

const ExportToProcountorComponentPage: React.FC<IProps> = (props: IProps) => {
    const selection = useInvoiceSelection();
    const expansion = useGroupExpansion();

    const handlePreviewLoaded = useCallback(
        (response: IProcountorInvoicePreviewResponse, preserveSelection: boolean) => {
            expansion.resetExpansion(response);
            selection.applyFromResponse(response, preserveSelection);
        },
        [expansion.resetExpansion, selection.applyFromResponse]
    );

    const previewState = useProcountorPreview({
        onLoaded: handlePreviewLoaded,
        onCleared: selection.clearSelection
    });

    const listControls = useInvoiceListControls({ onFilterChange: previewState.markStale });
    const procountorId = useProcountorIdGeneration();
    const brokerEditors = useBrokerNodeEditors(props.user);

    const actions = useInvoiceActions({
        fromDate: previewState.fromDate,
        toDate: previewState.toDate,
        directInvoices: previewState.directInvoices,
        selectedRowIds: selection.selectedRowIds,
        loadPreview: previewState.loadPreview
    });

    const visible = useVisibleInvoiceGroups({
        preview: previewState.preview,
        searchText: listControls.searchText,
        statusFilter: listControls.statusFilter,
        validityFilter: listControls.validityFilter,
        sortKey: listControls.sortKey,
        sortDirection: listControls.sortDirection,
        selectedRowIds: selection.selectedRowIds
    });

    const {
        preview: previewData,
        loadingPreview,
        isPreviewStale,
        fromDate,
        toDate,
        directInvoices,
        invoiceDates,
        selectedInvoiceDate,
        invoiceDateSelected
    } = previewState;
    const { selectedRowIds, toggleRowSelection, toggleMultipleRows } = selection;
    const { isGroupExpanded, toggleGroupExpansion, ensureExpanded } = expansion;
    const { searchText, statusFilter, validityFilter, sortKey, sortDirection, handleSortChange } = listControls;
    const {
        generatedProcountorIds,
        isGeneratingProcountorId,
        handleGenerateProcountorId,
        handleCopyGeneratedProcountorId,
        handleCopyProcountorIdAssignmentPhrase
    } = procountorId;
    const {
        showFirmModal,
        editingFirmNodeId,
        showAgentModal,
        editingAgentNodeId,
        openBrokerFirmEditor,
        openBrokerAgentEditor,
        closeFirmModal,
        closeAgentModal
    } = brokerEditors;
    const { runStatusUpdate, handleExportToCSV, handleRollbackExportedInvoices, handleMarkInvoicesAsCompleted } = actions;
    const {
        filteredGroups,
        firstInvalidGroupId,
        visibleRowIds,
        selectedAmountTotal,
        visibleAmountTotal,
        hasSelection,
        hasRows,
        allVisibleSelected,
        someVisibleSelected
    } = visible;

    const isBusy = loadingPreview || actions.actionInProgress;
    const canExport = !!previewData && hasSelection && visible.selectedAllValid && !isBusy;
    const canChangeStatuses = hasSelection && !isBusy;
    const canEditBrokerTree = Features.hasFeature(Features.UpdateBrokerTreeNode, props.user);

    const handleRowStatusChange = (orderRowId: number | string, invoiceStatusValue: number) => {
        runStatusUpdate([orderRowId], invoiceStatusValue, 'Invoice status updated');
    };

    const scrollToFirstInvalidGroup = () => {
        if (!firstInvalidGroupId) {
            return;
        }

        ensureExpanded(firstInvalidGroupId);

        const element = document.getElementById(`zentuvo-group-${firstInvalidGroupId}`);
        if (element && element.scrollIntoView) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    return (
        <Box className="zentuvo-invoicing-page" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h4">{Translate('zentuvo-invoicing/export-page-title')}</Typography>

            <Paper sx={{ padding: 3 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel id="zentuvo-invoice-date-label" shrink>
                            Invoice date
                        </InputLabel>
                        <Select
                            labelId="zentuvo-invoice-date-label"
                            value={selectedInvoiceDate}
                            displayEmpty
                            input={<OutlinedInput notched label="Invoice date" />}
                            onChange={(event) => previewState.handleInvoiceDateChange(event.target.value as string)}
                        >
                            <MenuItem value="">
                                <em>No invoice date</em>
                            </MenuItem>
                            {invoiceDates.map((invoiceDate) => (
                                <MenuItem key={invoiceDate} value={invoiceDate}>
                                    {invoiceDate}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <DatePicker
                        views={['day', 'month', 'year']}
                        label={Translate('zentuvo-invoicing/from-date-label')}
                        value={fromDate}
                        disabled={invoiceDateSelected}
                        onChange={(newValue) => {
                            previewState.setFromDate(newValue);
                            previewState.markStale();
                        }}
                        renderInput={(params) => <TextField {...params} helperText={null} fullWidth />}
                    />
                    <DatePicker
                        views={['day', 'month', 'year']}
                        label={Translate('zentuvo-invoicing/to-date-label')}
                        value={toDate}
                        disabled={invoiceDateSelected}
                        onChange={(newValue) => {
                            previewState.setToDate(newValue);
                            previewState.markStale();
                        }}
                        renderInput={(params) => <TextField {...params} helperText={null} fullWidth />}
                    />
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={directInvoices}
                                    onChange={(event) => {
                                        previewState.setDirectInvoices(event.target.checked);
                                        previewState.markStale();
                                    }}
                                />
                            }
                            label={Translate('zentuvo-invoicing/isdirect-label')}
                        />
                    </FormGroup>
                    <Button variant="outlined" disabled={isBusy} onClick={() => previewState.loadPreview(false)}>
                        Refresh Preview
                    </Button>
                </Stack>

                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} mt={3}>
                    <TextField
                        label="Search rows"
                        value={searchText}
                        onChange={(event) => {
                            listControls.setSearchText(event.target.value);
                        }}
                        fullWidth
                    />
                    <FormControl sx={{ minWidth: 180 }}>
                        <InputLabel id="zentuvo-status-filter-label">Status</InputLabel>
                        <Select
                            labelId="zentuvo-status-filter-label"
                            value={statusFilter}
                            label="Status"
                            onChange={(event) => {
                                listControls.setStatusFilter(event.target.value as string);
                            }}
                        >
                            {STATUS_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 180 }}>
                        <InputLabel id="zentuvo-validity-filter-label">Validity</InputLabel>
                        <Select
                            labelId="zentuvo-validity-filter-label"
                            value={validityFilter}
                            label="Validity"
                            onChange={(event) => {
                                listControls.setValidityFilter(event.target.value as string);
                            }}
                        >
                            <MenuItem value="all">All rows</MenuItem>
                            <MenuItem value="valid">Valid ProcountorId</MenuItem>
                            <MenuItem value="invalid">Missing ProcountorId</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            </Paper>

            {isPreviewStale && (
                <Alert severity="info">Filters or dates changed. Click "Refresh Preview" to fetch data from backend.</Alert>
            )}

            {previewData && previewData.HasValidationErrors && (
                <Alert
                    severity="error"
                    action={
                        firstInvalidGroupId ? (
                            <Button color="inherit" size="small" onClick={scrollToFirstInvalidGroup}>
                                Show problematic records
                            </Button>
                        ) : undefined
                    }
                >
                    Export is disabled because {previewData.InvalidRowCount} row(s) are missing a valid ProcountorId.
                    These records are listed at the top and expanded — fix them before exporting.
                </Alert>
            )}

            {loadingPreview && <Alert severity="info">Loading invoice preview...</Alert>}

            {!loadingPreview && previewData && (previewData.Groups || []).length === 0 && (
                <Alert severity="info">No invoice rows found for the selected range.</Alert>
            )}

            <Paper sx={{ padding: 3 }}>
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between">
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
                        <Chip
                            label={`Visible total: ${formatCurrency(visibleAmountTotal)}`}
                            color="primary"
                            variant="outlined"
                            sx={{ '.MuiChip-label': { userSelect: 'text' } }}
                        />
                        <Chip
                            label={`Selected total: ${formatCurrency(selectedAmountTotal)}`}
                            sx={{ '.MuiChip-label': { userSelect: 'text' } }}
                        />
                        {previewData && (
                            <Chip
                                label={`Preview rows: ${(previewData.Totals && previewData.Totals.TotalRowCount) || 0}`}
                                variant="outlined"
                                sx={{ '.MuiChip-label': { userSelect: 'text' } }}
                            />
                        )}
                        <Chip label={`Selected rows: ${selectedRowIds.length}`} sx={{ '.MuiChip-label': { userSelect: 'text' } }} />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={allVisibleSelected}
                                    indeterminate={!allVisibleSelected && someVisibleSelected}
                                    disabled={visibleRowIds.length === 0}
                                    onChange={() => toggleMultipleRows(visibleRowIds)}
                                />
                            }
                            label="Select visible rows"
                        />
                        <Button onClick={handleExportToCSV} variant="contained" disabled={!canExport}>
                            Export to CSV
                        </Button>
                        <Button onClick={handleRollbackExportedInvoices} variant="outlined" disabled={!canChangeStatuses}>
                            Rollback Exported
                        </Button>
                        <Button onClick={handleMarkInvoicesAsCompleted} variant="outlined" disabled={!canChangeStatuses}>
                            Mark As Completed
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {hasRows &&
                filteredGroups.map((visibleGroup) => (
                    <InvoiceGroupAccordion
                        key={visibleGroup.group.GroupId}
                        visibleGroup={visibleGroup}
                        isExpanded={isGroupExpanded(visibleGroup.group.GroupId)}
                        selectedRowIds={selectedRowIds}
                        sortKey={sortKey}
                        sortDirection={sortDirection}
                        canEditBrokerTree={canEditBrokerTree}
                        generatedProcountorIds={generatedProcountorIds}
                        isGeneratingProcountorId={isGeneratingProcountorId}
                        onToggleExpansion={toggleGroupExpansion}
                        onToggleRow={toggleRowSelection}
                        onToggleGroupRows={toggleMultipleRows}
                        onRowStatusChange={handleRowStatusChange}
                        onSortChange={handleSortChange}
                        onEditAgent={openBrokerAgentEditor}
                        onEditFirm={openBrokerFirmEditor}
                        onGenerateProcountorId={handleGenerateProcountorId}
                        onCopyProcountorId={handleCopyGeneratedProcountorId}
                        onCopyAssignmentPhrase={handleCopyProcountorIdAssignmentPhrase}
                    />
                ))}
            {!hasRows && !loadingPreview && (
                <Paper sx={{ padding: 3 }}>
                    <Typography variant="body1">Choose a date range to load invoice rows and review them before export.</Typography>
                </Paper>
            )}

            {showFirmModal && editingFirmNodeId && <FirmModal onClose={closeFirmModal} item={{ id: editingFirmNodeId }} />}
            {showAgentModal && editingAgentNodeId && <AgentModal onClose={closeAgentModal} item={{ id: editingAgentNodeId }} />}
        </Box>
    );
};

export default ExportToProcountorComponentPage;
